import React, {
	Component,
	createContext,
	type ReactNode,
	useContext,
	useMemo,
	useState,
} from 'react';

const ErrorBoundaryBypassApiContext = createContext<null | {
	swallowError: (error: Error) => void;
}>(null);
const ErrorBoundaryBypassErrorContext = createContext<null | Error>(null);

export const ErrorBoundaryBypassProvider = ({ children }: { children: ReactNode }) => {
	const [bypassedError, setBypassedError] = useState<Error | null>(null);

	const api = useMemo(
		() => ({
			swallowError: (error: Error) => {
				setBypassedError(error);
			},
		}),
		[],
	);

	return (
		<ErrorBoundaryBypassErrorContext.Provider value={bypassedError}>
			<ErrorBoundaryBypassApiContext.Provider value={api}>
				{children}
			</ErrorBoundaryBypassApiContext.Provider>
		</ErrorBoundaryBypassErrorContext.Provider>
	);
};

// eslint-disable-next-line @repo/internal/react/no-class-components
export class ErrorBoundaryBypassInput extends Component<
	{
		children: ReactNode;
	},
	{
		hasError: boolean;
	}
> {
	static getDerivedStateFromError() {
		// Update state so the next render will show the fallback UI.
		return { hasError: true };
	}

	state = { hasError: false };

	componentDidCatch(error: Error) {
		if (this.context !== null) {
			this.context.swallowError(error);
		}
	}

	context!: React.ContextType<typeof ErrorBoundaryBypassApiContext>;

	static contextType = ErrorBoundaryBypassApiContext;

	render() {
		if (this.state.hasError) {
			return null;
		}
		return this.props.children;
	}
}

export const ErrorBoundaryBypassOutput = () => {
	const bypassedError = useContext(ErrorBoundaryBypassErrorContext);

	if (bypassedError !== null) {
		throw bypassedError;
	}

	return null;
};
